import { useEffect, useState } from 'react';
import { Eye, EyeOff, KeyRound, ShieldCheck } from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import { alwaysdataConfigPatchSchema } from '@jeraisonne/db';
import {
  AlwaysdataError,
  useAlwaysdataConfig,
  useUpdateAlwaysdataConfig,
} from '@/lib/admin/alwaysdata';

const inputCls =
  'border-ink-200 text-ink-900 placeholder:text-ink-400 focus:ring-primary-400 w-full rounded-xl border px-3 py-2.5 font-sans text-[14px] focus:outline-none focus:ring-2';
const labelCls = 'font-display text-ink-700 text-[12px] font-semibold';
const helpCls = 'text-ink-500 font-sans text-[12px]';

/**
 * Panneau de config API Alwaysdata.
 *
 * Le TOKEN est en champ password : jamais rendu à la lecture, on affiche
 * seulement « défini le … » quand `token_set=true`. L'admin peut le remplacer
 * (nouveau token → Vault, ancien écrasé) mais pas le voir.
 */
export function AlwaysdataConfigPanel() {
  const { data: config, isLoading } = useAlwaysdataConfig();
  const update = useUpdateAlwaysdataConfig();

  const [apiUrl, setApiUrl] = useState('');
  const [domainName, setDomainName] = useState('');
  const [allowedIps, setAllowedIps] = useState('');
  const [token, setToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  // Hydrate le formulaire quand la config arrive.
  useEffect(() => {
    if (!config) return;
    setApiUrl(config.api_url);
    setDomainName(config.domain_name);
    setAllowedIps(config.allowed_ips ?? '');
  }, [config]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setOk(false);

    const patch = alwaysdataConfigPatchSchema.safeParse({
      api_url: apiUrl.trim() || undefined,
      domain_name: domainName.trim() || undefined,
      allowed_ips: allowedIps.trim() ? allowedIps.trim() : null,
      token: token.trim() || undefined,
    });
    if (!patch.success) {
      setError(patch.error.issues[0]?.message ?? 'validation invalide');
      return;
    }

    try {
      await update.mutateAsync(patch.data);
      setToken('');
      setOk(true);
    } catch (err) {
      if (err instanceof AlwaysdataError) setError(err.message);
      else setError(err instanceof Error ? err.message : String(err));
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="border-ink-200 flex flex-col gap-4 rounded-2xl border bg-white p-4 sm:p-5"
    >
      <div className="flex items-center gap-2">
        <ShieldCheck className="text-primary-500 h-5 w-5" />
        <p className="font-display text-ink-900 text-[15px] font-bold">Connexion API Alwaysdata</p>
      </div>

      <div className="flex flex-col gap-1">
        <label className={labelCls} htmlFor="ad-api-url">
          URL de l'API
        </label>
        <input
          id="ad-api-url"
          className={inputCls}
          value={apiUrl}
          onChange={(e) => setApiUrl(e.target.value)}
          placeholder="https://api.alwaysdata.com/v1"
          autoComplete="off"
        />
      </div>

      <div className="flex flex-col gap-1">
        <label className={labelCls} htmlFor="ad-domain">
          Domaine géré
        </label>
        <input
          id="ad-domain"
          className={inputCls}
          value={domainName}
          onChange={(e) => setDomainName(e.target.value)}
          placeholder="jeraisonne.fr"
          autoComplete="off"
        />
        <p className={helpCls}>
          Seules les boîtes de ce domaine seront listées et modifiables depuis cet écran.
        </p>
      </div>

      <div className="flex flex-col gap-1">
        <label className={labelCls} htmlFor="ad-token">
          Token API
        </label>
        <div className="relative">
          <input
            id="ad-token"
            className={`${inputCls} pr-10`}
            type={showToken ? 'text' : 'password'}
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder={
              config?.token_set ? '••••••••  (déjà défini — saisir pour remplacer)' : 'Token API'
            }
            autoComplete="new-password"
          />
          <button
            type="button"
            aria-label={showToken ? 'Masquer le token' : 'Afficher le token'}
            onClick={() => setShowToken((v) => !v)}
            className="text-ink-400 hover:text-ink-700 absolute inset-y-0 right-2 flex items-center"
          >
            {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        <p className={helpCls}>
          <KeyRound className="mr-1 inline h-3 w-3" />
          Créé dans l'admin Alwaysdata → Profil → Sécurité → Tokens. Contourne le 2FA. Stocké
          chiffré (Vault), jamais renvoyé côté client après l'enregistrement.
        </p>
      </div>

      <div className="flex flex-col gap-1">
        <label className={labelCls} htmlFor="ad-ips">
          IPs autorisées <span className="text-ink-400 font-normal">(indicatif)</span>
        </label>
        <input
          id="ad-ips"
          className={inputCls}
          value={allowedIps}
          onChange={(e) => setAllowedIps(e.target.value)}
          placeholder="192.0.2.42 198.51.100.0/24"
          autoComplete="off"
        />
        <p className={helpCls}>
          Ce champ n'est pas envoyé à Alwaysdata — le filtrage IP se règle dans l'admin Alwaysdata.
          On garde ici une trace pour info.
        </p>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3 font-sans text-[13px] text-red-800">
          {error}
        </div>
      )}
      {ok && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 font-sans text-[13px] text-emerald-800">
          Config enregistrée{token ? ' (nouveau token stocké dans Vault)' : ''}.
        </div>
      )}

      <div className="flex items-center gap-3">
        <Button type="submit" disabled={update.isPending}>
          {update.isPending ? 'Enregistrement…' : 'Enregistrer'}
        </Button>
        <span className="text-ink-500 font-sans text-[12px]">
          {config?.token_set ? 'Token défini' : 'Aucun token défini'}
          {config?.updated_at
            ? ` · mis à jour ${new Date(config.updated_at).toLocaleString('fr-FR')}`
            : ''}
        </span>
      </div>
    </form>
  );
}
